import {
  ApiOutlined,
  DisconnectOutlined,
  ReloadOutlined,
  WifiOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Col,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AppLayout from "../components/layout/AppLayout";
import BreadcrumbNav from "../components/layout/BreadcrumbNav";
import type { Device, Stats } from "../types";
import { apiClient } from "../utils/api";
import { useWebSocket } from "../utils/useWebSocket";

const { Title } = Typography;

interface DevicesPageProps {
  protocolFilter?: "tcp" | "mqtt";
}

const defaultStats: Stats = {
  total_devices: 0,
  online_devices: 0,
  offline_devices: 0,
  total_connections: 0,
  active_connections: 0,
  bytes_received: 0,
  bytes_sent: 0,
  uptime: 0,
};

const DevicesPage: React.FC<DevicesPageProps> = ({ protocolFilter }) => {
  const queryClient = useQueryClient();

  const {
    data: devices = [],
    isLoading: devicesLoading,
    refetch: refetchDevices,
  } = useQuery<Device[]>({
    queryKey: ["devices", protocolFilter],
    queryFn: async () => {
      if (protocolFilter === "tcp") {
        return (await apiClient.getTCPDevices()) || [];
      } else if (protocolFilter === "mqtt") {
        return (await apiClient.getMQTTDevices()) || [];
      } else {
        const [tcpDevices, mqttDevices] = await Promise.all([
          apiClient.getTCPDevices(),
          apiClient.getMQTTDevices(),
        ]);
        return [...(tcpDevices || []), ...(mqttDevices || [])];
      }
    },
    refetchInterval: 30000,
  });

  const {
    data: stats = defaultStats,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useQuery<Stats>({
    queryKey: ["stats", protocolFilter],
    queryFn: async () => {
      return (await apiClient.getStats()) as Stats;
    },
    refetchInterval: 30000,
  });

  const loading = devicesLoading || statsLoading;

  const handleRefresh = () => {
    refetchDevices();
    refetchStats();
  };

  const { connectionStatus } = useWebSocket(undefined, {
    onMessage: (msg) => {
      if (msg.type === "weight_update" || msg.type === "ptl_event" ||
          msg.type === "ptl_confirm" || msg.type === "ptl_button_press" ||
          msg.type === "device_command") {
        queryClient.invalidateQueries({ queryKey: ["devices"] });
        queryClient.invalidateQueries({ queryKey: ["stats"] });
      }
    },
  });

  const deviceColumns: ColumnsType<Device> = [
    {
      title: "Device ID",
      dataIndex: "id",
      key: "id",
      width: 120,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 150,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (type: string) => {
        const colorMap: Record<string, string> = {
          scale: "blue",
          ptl: "purple",
          inkjet_printer: "orange",
          dataman: "cyan",
        };
        const labelMap: Record<string, string> = {
          scale: "Scale",
          ptl: "PTL",
          inkjet_printer: "Inkjet",
          dataman: "DataMan",
        };
        return <Tag color={colorMap[type] || "default"}>{labelMap[type] || type}</Tag>;
      },
    },
    {
      title: "Protocol",
      dataIndex: "protocol",
      key: "protocol",
      width: 80,
      render: (protocol: string) => {
        const p = protocol || "tcp";
        const colorMap: Record<string, string> = { tcp: "blue", mqtt: "green", websocket: "purple" };
        return (
          <Tag
            color={colorMap[p] || "default"}
            icon={p === "tcp" ? <ApiOutlined /> : <WifiOutlined />}
          >
            {p.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => (
        <Badge
          status={status === "online" ? "success" : "error"}
          text={
            <Tag color={status === "online" ? "green" : "red"}>
              {status.toUpperCase()}
            </Tag>
          }
        />
      ),
    },
    {
      title: "IP/Port",
      key: "ip_port",
      width: 150,
      render: (_, record) => (
        <span>
          {record.ip || "-"}:{record.port || "-"}
        </span>
      ),
    },
    {
      title: "Last Seen",
      dataIndex: "last_seen",
      key: "last_seen",
      width: 180,
    },
  ];

  return (
    <AppLayout
      title={protocolFilter === "tcp" ? "TCP Devices" : protocolFilter === "mqtt" ? "MQTT Devices" : "Device Management"}
      extraActions={
        <Space>
          <Tooltip title={`Real-time updates: ${connectionStatus}`}>
            <div className="flex items-center gap-4">
              <Badge
                status={
                  connectionStatus === "connected"
                    ? "processing"
                    : connectionStatus === "connecting"
                    ? "warning"
                    : "error"
                }
              />{" "}
              {connectionStatus === "connected"
                ? "Live"
                : connectionStatus === "connecting"
                ? "Connecting..."
                : "Offline"}
            </div>
          </Tooltip>
          <Tooltip title="Refresh data">
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>
          </Tooltip>
        </Space>
      }
    >
      <BreadcrumbNav />

      <div style={{ marginBottom: "24px" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Devices"
                value={stats.total_devices}
                prefix={<ApiOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Online Devices"
                value={stats.online_devices}
                valueStyle={{ color: "#3f8600" }}
                prefix={<WifiOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Offline Devices"
                value={stats.offline_devices}
                valueStyle={{ color: "#cf1322" }}
                prefix={<DisconnectOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Active Connections"
                value={stats.active_connections}
                prefix={<WifiOutlined />}
              />
            </Card>
          </Col>
        </Row>
      </div>

      <Card
        title="Connected Devices"
        extra={
          <Space>
            <Badge
              count={devices.filter((d) => d.status === "online").length}
              style={{ backgroundColor: "#52c41a" }}
            >
              <Tag color="green">Online</Tag>
            </Badge>
            <Badge
              count={devices.filter((d) => d.status === "offline").length}
              style={{ backgroundColor: "#ff4d4f" }}
            >
              <Tag color="red">Offline</Tag>
            </Badge>
          </Space>
        }
      >
        <Table
          columns={deviceColumns}
          dataSource={devices}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} devices`,
          }}
          scroll={{ x: 800 }}
        />
      </Card>
    </AppLayout>
  );
};

export default DevicesPage;
